import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../shared/table-builder/table-ag-client'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { LocalTime } from '../../../../shared/date/local-time-view'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { IconList } from '../../../../shared/icon/icon'
import { getCatalog, billingAvailable } from './billing-api'
import { BillingUnavailable } from './unavailable'
import { isHidden, planOrder, valueText } from './format'

// One row per feature, grouped by category (the categories' order, then the
// features'), one column per plan.
const rowsOf = contents => {
  const cats = contents.cats || {}
  const features = contents.features || {}
  const catOrder = k => (cats[k] && cats[k].order) || 0
  return Object.keys(features)
    .sort((a, b) => (catOrder(features[a].cat) - catOrder(features[b].cat)) || ((features[a].order || 0) - (features[b].order || 0)) || a.localeCompare(b))
    .map((key, i) => {
      const f = features[key]
      const row = { id: key, seq: i, category: (cats[f.cat] && cats[f.cat].name) || f.cat || '', feature: f.name || key }
      Object.keys(f.values || {}).forEach(plan => { row[`plan_${plan}`] = valueText(f.values[plan], f.unit) })
      return row
    })
}

const getRowId = params => params.data.id
const NONE = { Icon: IconList, title: 'No features', message: 'This catalog version has no features.' }

const CatalogView = ({ published }) => {
  const tableRef = useRef()
  const contents = useMemo(() => published.contents || {}, [published])
  const plans = useMemo(() => contents.plans || {}, [contents])
  const order = useMemo(() => planOrder(plans), [plans])
  const apiList = useMemo(() => ({ items: rowsOf(contents), loaded: true, loading: false, error: undefined }), [contents])
  return (
    <FleetPage title="Plan catalog" purpose={<>Plan catalog · version {published.n} · published <LocalTime value={published.publishedAt} /></>}>
      <Notice tone="warn">Shadow mode: plans don't decide access yet.</Notice>
      <FleetGrid title="catalog" tableRef={tableRef} exportName="billing-catalog">
        <TableAgClient ref={tableRef} apiList={apiList} queryKey="" sortCol="seq" getRowId={getRowId} emptyState={NONE}>
          <ColumnFormat field="seq" header="#" hide sortable={false} render={r => (r.data ? String(r.value) : '')} />
          <ColumnFormat field="category" header="Category" sortable={false} minWidth={170} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="feature" header="Feature" sortable={false} minWidth={200} render={r => (r.data ? r.value : '')} />
          {order.map(key => (
            <ColumnFormat key={key} field={`plan_${key}`} header={`${plans[key].name || key}${isHidden(plans[key]) ? ' (hidden)' : ''}`} sortable={false} minWidth={130}
              render={r => (r.data ? r.value : '')} />
          ))}
        </TableAgClient>
      </FleetGrid>
    </FleetPage>
  )
}

const CatalogLoader = () => {
  const [catalog, setCatalog] = useState(null)
  const [error, failure, setError] = useErrorLine()
  useEffect(() => {
    let live = true
    getCatalog().then(
      v => { if (live) setCatalog(v) },
      err => { if (live) setError(serverMessage(err, "The plan catalog couldn't be loaded."), err) }
    )
    return () => { live = false }
  }, [setError])
  if (error) return <FleetPage title="Plan catalog"><Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice></FleetPage>
  if (!catalog || !catalog.published) return <FleetPage title="Plan catalog"><p className="sw-hint">Loading…</p></FleetPage>
  return <CatalogView published={catalog.published} />
}

export const CatalogPage = () => (billingAvailable() ? <CatalogLoader /> : <BillingUnavailable title="Plan catalog" />)
